"use client"

import { useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../shared/functions/Context"
import ValueRatingApi from "../../../shared/apis/three-sixty-apis/ValueRatingApi"
import type { IUser } from "../../../shared/models/User"
import { Target, User, Building2, Briefcase, Activity, Award, Star } from "lucide-react"

interface ValueRatings {
  [key: string]: {
    [key: string]: number[]
  }
}

const Dimension = observer(() => {
  const { api, store } = useAppContext()
  const valueRatingApi = new ValueRatingApi(api, store)
  const [valueRatings, setValueRatings] = useState<ValueRatings>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        await api.user.getAll()
        await api.department.getAll()
        await api.ratingAssignments.getAll()
        const users = store.user.all.map((user) => user.asJson)
        const description = store.ratingAssignments.all.find((rate) => rate.asJson.status === "In Progress")?.asJson
          .description
        const uids = store.user.all.map((user) => user.asJson.uid)

        if (description && users.length) {
          const fetchedRatings = await valueRatingApi.getAllValueRatings(description, uids)
          const transformedRatings: ValueRatings = {}

          fetchedRatings.forEach((rating) => {
            Object.entries(rating.values).forEach(([raterId, raterData]) => {
              Object.entries(raterData.ratings).forEach(([valueName, statements]) => {
                const ratingValues = Object.values(statements).flat()
                if (!transformedRatings[valueName]) {
                  transformedRatings[valueName] = {}
                }
                if (!transformedRatings[valueName][raterId]) {
                  transformedRatings[valueName][raterId] = []
                }
                transformedRatings[valueName][raterId] = [...ratingValues]
              })
            })
          })

          setValueRatings(transformedRatings)
        } else {
          setError("Description or users not found")
        }
      } catch (error) {
        setError("Error fetching data")
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [api, store])

  const getUserDetails = (userId: string): IUser | null => {
    const user = store.user.all.find((user) => user.asJson.uid === userId)?.asJson
    return user || null
  }

  const getDisplayNameById = (userId: string) => {
    const user = store.user.all.find((user) => user.asJson.uid === userId)?.asJson
    return user ? user.displayName : undefined
  }

  const getDepartmentName = (departmentId: string): string => {
    const department = store.department.all.find((department) => department.asJson.id === departmentId)?.asJson
    return department ? (department.name ?? "") : ""
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-200"></div>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent absolute top-0 left-0"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Activity className="h-5 w-5 text-purple-600 animate-pulse" />
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <div className="text-center space-y-4">
          <div className="p-4 bg-red-100 rounded-full w-fit mx-auto">
            <Target className="h-8 w-8 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Error Loading Data</h3>
            <p className="text-slate-600">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-purple-100 rounded-lg">
          <Target className="h-5 w-5 text-purple-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Top Performers by Dimension</h2>
          <p className="text-slate-600">Highest rated employees for each value dimension</p>
        </div>
      </div>

      {/* Dimension Cards */}
      <div className="grid gap-6">
        {Object.entries(valueRatings).map(([valueName, raterRatings]) => {
          const topRatedRatee = Object.entries(raterRatings).reduce(
            (maxRatee, [rateeId, ratings]) => {
              const averageRating = ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length
              return averageRating > maxRatee.averageRating ? { rateeId, averageRating } : maxRatee
            },
            { rateeId: "", averageRating: 0 },
          )

          if (!topRatedRatee.rateeId) {
            return null
          }

          const rateeDetails = getUserDetails(topRatedRatee.rateeId)
          const rateeDisplayName = getDisplayNameById(topRatedRatee.rateeId)
          const rateeDepartment = rateeDetails ? getDepartmentName(rateeDetails.department) : ""

          return (
            <div key={valueName} className="bg-white rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
              {/* Value Header */}
              <div className="bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 p-6 border-b border-purple-100/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-lg">
                      <Target className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-800">{valueName}</h3>
                      <p className="text-slate-600">Top performer in this dimension</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Star className="h-5 w-5 text-amber-500" />
                    <span className="text-2xl font-bold text-slate-800">{topRatedRatee.averageRating.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Employee Details */}
              <div className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <User className="h-8 w-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-slate-800 mb-2">{rateeDisplayName}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Building2 className="h-4 w-4 text-blue-600" />
                        <span className="font-medium">Department:</span>
                        <span>{rateeDepartment}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Briefcase className="h-4 w-4 text-purple-600" />
                        <span className="font-medium">Job Title:</span>
                        <span>{rateeDetails?.jobTitle}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="p-3 bg-purple-100 rounded-xl">
                      <Award className="h-6 w-6 text-purple-600 mx-auto mb-1" />
                      <p className="text-xs font-medium text-purple-800">Top Rated</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Empty State */}
      {Object.keys(valueRatings).length === 0 && (
        <div className="text-center py-12">
          <div className="p-4 bg-purple-100 rounded-full w-fit mx-auto mb-4">
            <Target className="h-12 w-12 text-purple-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Dimension Data Available</h3>
          <p className="text-slate-600">Complete some ratings to see top performers by dimension.</p>
        </div>
      )}
    </div>
  )
})

export default Dimension
